<script setup lang="ts">
import { useEventListener, computed, definePageMeta, useTemplateRef, useElementSize, whenever, useUserNotifications, usePreviewBridge } from '#imports'
import type { MessagesComponents, MessageType } from '@core/types'
import { z } from 'zod/v4'
import { previewDialogComponent } from '@nimbus/components/Notification/Dialog/dialogComponents'

definePageMeta({
    layout: false,
    hideOverlayContainer: true,
    preview: true,
})

type RenderedNotification = MessagesComponents['schemas']['RenderedNotification']

// Loose schema: validate the fields we render, allow the rest through.
const ActionSchema = z.looseObject({
    text: z.string(),
    callback: z.string().default(''),
    redirect: z.string().nullable().default(null),
})

const DataSchema = z.looseObject({
    title: z.string().default(''),
    body: z.string().nullable().default(null),
    presentation_type: z.string().nullable(),
    type: z.string().optional(),
    acceptAction: ActionSchema.nullable().default(null),
    dismissAction: ActionSchema.nullable().default(null),
})
const { data, send } = usePreviewBridge<typeof DataSchema, { type: 'link', payload: { href: string, x: number, y: number } }>(DataSchema)

// The query is disabled; we only need getIcon to mirror the notification-center card.
const { getIcon } = useUserNotifications({ enabled: false })

const isFlash = computed(() => data.value?.success && data.value.data.presentation_type === 'flash')
const isNotification = computed(() => data.value?.success && data.value.data.presentation_type === 'notification')
// Flash and the notification-center card render in normal flow; dialogs overlay the viewport.
const isInline = computed(() => isFlash.value || isNotification.value)

// Preview a type as its own dialog, where that renders from the notification alone.
const dialogComponent = computed(() => {
    const type = data.value?.success ? data.value.data.type : undefined

    return previewDialogComponent(type as MessageType | undefined)
})

// Links (body anchors, action buttons with a redirect) must not navigate the
// iframe away from the preview; hand them to the embedder, which lets the
// user pick the brand site to open them on.
const lastPointer = { x: 0, y: 0 }

function actionRedirectFor(button: HTMLElement): string | null {
    const text = button.textContent?.trim()
    if (!text || !data.value?.success) return null

    const { acceptAction, dismissAction } = data.value.data

    for (const action of [acceptAction, dismissAction]) {
        if (action?.redirect && action.text.trim() === text) return action.redirect
    }

    return null
}

if (import.meta.client) {
    useEventListener(window, 'click', (event: MouseEvent) => {
        lastPointer.x = event.clientX
        lastPointer.y = event.clientY

        const target = event.target as HTMLElement | null
        const anchor = target?.closest<HTMLAnchorElement>('a[href]')
        const button = anchor ? null : target?.closest<HTMLElement>('button')
        const href = anchor?.getAttribute('href') ?? (button ? actionRedirectFor(button) : null)

        if (!href) return

        event.preventDefault()
        event.stopPropagation()
        send({ type: 'link', payload: { href, x: event.clientX, y: event.clientY } })
    }, { capture: true })
}

const content = useTemplateRef<HTMLDivElement>('content')
const { height } = useElementSize(content)

// Inline content (flash, notification-center card) reports its measured height as it changes.
whenever(height, (h) => {
    if (isInline.value) send({ type: 'resize', payload: { height: `${h + 16}px`, width: '414px' } })
})

// Dialog renders a full-screen backdrop (height 0 in flow, so the measured
// height never triggers above); report a fixed viewport height once the data
// parses so the centered modal + backdrop are visible.
whenever(() => data.value?.success && !isInline.value, () => {
    send({ type: 'resize', payload: { height: '560px', width: '414px' } })
})

// Fallback for card buttons the capture listener did not match (a matched
// click never reaches this emit): report the redirect instead of navigating.
function onCardAction({ action }: { action: { redirect?: string | null } | null }) {
    if (action?.redirect) send({ type: 'link', payload: { href: action.redirect, x: lastPointer.x, y: lastPointer.y } })
}
</script>

<template>
    <UIApp>
        <div v-if="!data">
            <UILoader bg>
                Waiting for data
            </UILoader>
        </div>

        <template v-else>
            <div v-if="data.error">
                <pre>{{ data.error.issues }}</pre>
            </div>

            <div v-else ref="content" :class="{ 'preview-content--inline': isInline }">
                <NimbusNotificationFlashDefaultMessage
                    v-if="isFlash"
                    :notification="(data.data as unknown as RenderedNotification)"
                    @dismiss="() => {}"
                />
                <!-- 'notification' never pops up in the player; it lands in the
                     notification center, so mirror that card. Its mutations flow
                     through emits we deliberately leave unbound. -->
                <PromotionUserNotificationCard
                    v-else-if="isNotification"
                    :notification="(data.data as unknown as RenderedNotification)"
                    view="card"
                    :errors="{}"
                    :icon="getIcon(data.data.type ?? '')"
                    @call-action="onCardAction"
                />
                <component
                    :is="dialogComponent"
                    v-else
                    :notification="(data.data as unknown as RenderedNotification)"
                />
            </div>
        </template>
    </UIApp>
</template>

<style lang="scss">
// Standalone preview page (layout: false): match the notification dropdown's
// white panel so the card keeps its real gray-on-white contrast.
body {
    background-color: var(--surface, $white);
}
</style>

<style scoped lang="scss">
// Inline content (flash, notification-center card) floats like on a real
// device instead of hugging the viewport edges.
.preview-content--inline {
    padding: 16px;
}
</style>
